import { formatMonthYear } from './format';
import type { LatLon } from './geo';
import { endDateOf, isMoveType, type PlaceLookup, startDateOf } from './items';
import type { Item, Trip } from './schema';
import { addDays, compareDates, datesBetween, diffDays, localDate, parseLocal } from './time';

export type TripPhase = 'upcoming' | 'current' | 'past';

export interface TripLeg {
  itemId: string;
  kind: 'flight' | 'ground' | 'sea';
  from: LatLon & { code: string | null; name: string };
  to: LatLon & { code: string | null; name: string };
}

export interface TripSummary {
  id: string;
  title: string;
  autoTitle: string;
  startDate: string | null;
  endDate: string | null;
  /** Calendar days, counting both ends. */
  days: number;
  nights: number;
  phase: TripPhase;
  /** Days until it starts (upcoming) or the current day number (current). */
  daysUntil: number | null;
  dayNumber: number | null;
  /** Cities with nights, most nights first. */
  cities: { name: string; nights: number }[];
  /** Every city touched, in visit order. */
  places: string[];
  legs: TripLeg[];
  itemCount: number;
  flightCount: number;
  hasNotes: boolean;
}

/** Nights spent per city, from stays. */
export function nightsByCity(items: Item[], places: PlaceLookup): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const i of items) {
    if (i.deleted || i.status !== 'active' || i.type !== 'lodging') continue;
    const s = startDateOf(i);
    const e = endDateOf(i);
    const city = places(i.startPlaceId)?.city;
    if (!s || !e || !city) continue;
    for (let d = s; d < e; d = addDays(d, 1)) {
      const list = out.get(city) ?? [];
      if (!list.includes(d)) list.push(d);
      out.set(city, list);
    }
  }
  return out;
}

/** 'Minneapolis, October 2026': where most nights are spent, in the month with most nights. */
export function autoTripTitle(items: Item[], places: PlaceLookup): string {
  const live = items.filter((i) => !i.deleted && i.status === 'active');
  const byCity = nightsByCity(live, places);
  let city: string | null = null;
  let nights: string[] = [];
  for (const [c, dates] of byCity) {
    if (dates.length > nights.length) {
      city = c;
      nights = dates;
    }
  }
  if (!city) {
    // No stays: name it after the first destination.
    const sorted = [...live].sort((a, b) => (a.startAt ?? 0) - (b.startAt ?? 0));
    const move = sorted.find((i) => isMoveType(i.type));
    city =
      (move ? places(move.endPlaceId)?.city : null) ??
      places(sorted[0]?.startPlaceId)?.city ??
      null;
  }
  const allNights = [...byCity.values()].flat();
  let year: number | null = null;
  let month: number | null = null;
  if (allNights.length > 0) {
    const counts = new Map<string, number>();
    for (const d of allNights) counts.set(d.slice(0, 7), (counts.get(d.slice(0, 7)) ?? 0) + 1);
    const best = [...counts.entries()].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
    )[0]![0];
    year = Number(best.slice(0, 4));
    month = Number(best.slice(5, 7));
  } else {
    const first = live.map(startDateOf).filter(Boolean).sort()[0];
    if (first) {
      const p = parseLocal(first);
      year = p.year;
      month = p.month;
    }
  }
  // Named for the place; the dates show beside the name everywhere it appears.
  if (city) return city;
  return year && month ? formatMonthYear(year, month) : 'New trip';
}

export function legOf(i: Item, places: PlaceLookup): TripLeg | null {
  if (!isMoveType(i.type) && i.type !== 'car' && i.type !== 'transfer') return null;
  const a = places(i.startPlaceId);
  const b = places(i.endPlaceId);
  if (!a || !b || a.lat == null || a.lon == null || b.lat == null || b.lon == null) return null;
  if (a.id === b.id) return null;
  return {
    itemId: i.id,
    kind:
      i.type === 'flight' ? 'flight' : i.type === 'cruise' || i.type === 'ferry' ? 'sea' : 'ground',
    from: { lat: a.lat, lon: a.lon, code: a.iata, name: a.city ?? a.name },
    to: { lat: b.lat, lon: b.lon, code: b.iata, name: b.city ?? b.name },
  };
}

export function summarizeTrip(
  trip: Trip,
  items: Item[],
  places: PlaceLookup,
  today: string,
  home?: string | null,
): TripSummary {
  const live = items.filter((i) => !i.deleted);
  const active = live.filter((i) => i.status === 'active');
  const starts = active
    .map(startDateOf)
    .filter((d): d is string => !!d)
    .sort(compareDates);
  const ends = active
    .map(endDateOf)
    .filter((d): d is string => !!d)
    .sort(compareDates);
  const startDate = trip.startDate ?? starts[0] ?? null;
  const endDate = trip.endDate ?? ends[ends.length - 1] ?? null;
  const days = startDate && endDate ? diffDays(startDate, endDate) + 1 : 0;
  let phase: TripPhase = 'upcoming';
  if (endDate && endDate < today) phase = 'past';
  else if (startDate && startDate <= today) phase = 'current';
  const byCity = nightsByCity(active, places);
  const cities = [...byCity.entries()]
    .map(([name, dates]) => ({ name, nights: dates.length }))
    .sort((a, b) => b.nights - a.nights);
  const visit: string[] = [];
  for (const i of [...active].sort((a, b) => (a.startAt ?? 0) - (b.startAt ?? 0))) {
    for (const pid of isMoveType(i.type) ? [i.endPlaceId] : [i.startPlaceId]) {
      const c = places(pid)?.city;
      if (c && c !== home && !visit.includes(c)) visit.push(c);
    }
  }
  const legs = [...active]
    .sort((a, b) => (a.startAt ?? 0) - (b.startAt ?? 0))
    .map((i) => legOf(i, places))
    .filter((l): l is TripLeg => !!l);
  const autoTitle = autoTripTitle(live, places);
  const nights = startDate && endDate ? Math.max(0, diffDays(startDate, endDate)) : 0;
  return {
    id: trip.id,
    title: trip.title?.trim() || autoTitle,
    autoTitle,
    startDate,
    endDate,
    days,
    nights,
    phase,
    daysUntil: phase === 'upcoming' && startDate ? diffDays(today, startDate) : null,
    dayNumber: phase === 'current' && startDate ? diffDays(startDate, today) + 1 : null,
    cities,
    places: visit,
    legs,
    itemCount: live.length,
    flightCount: active.filter((i) => i.type === 'flight').length,
    hasNotes: trip.notes.trim().length > 0,
  };
}

/** Dates covered by a trip, inclusive. */
export function tripDates(summary: Pick<TripSummary, 'startDate' | 'endDate'>): string[] {
  if (!summary.startDate || !summary.endDate) return [];
  return datesBetween(summary.startDate, summary.endDate);
}

/** Local date 'today' for someone in a zone. */
export function todayIn(tz: string, now = Date.now()): string {
  return localDate(now, tz);
}
